/**
 * Fax Provider Settings — multi-provider fax integration.
 * Lets each agency pick between eFax Corporate and SRFax, and manage credentials
 * for both. The "active" provider is what CarePilot will use when sending faxes.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Printer,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Save,
  Send,
  Eye,
  EyeOff,
  ExternalLink,
  Info,
} from "lucide-react";

import EfaxEmailProviderTab from "@/components/settings/EfaxEmailProviderTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HELP_LINKS = {
  efax_email: {
    signup: "https://www.efax.com/",
    docs: "https://www.efax.com/features/email-to-fax",
    label: "eFax (regular plan, via email)",
    price: "Your existing eFax plan — no API add-on",
    hipaa: "BAA available on eFax Protect/Pro plans",
  },
  efax: {
    signup: "https://enterprise.efax.com/",
    docs: "https://www.efaxcorporate.com/efax-product/api",
    label: "eFax Corporate",
    price: "$30–60/mo (Corporate API plan required)",
    hipaa: "BAA available on request",
  },
  srfax: {
    signup: "https://www.srfax.com/plans-pricing/",
    docs: "https://www.srfax.com/developers/internet-fax-api/",
    label: "SRFax",
    price: "From $12.60/mo (Healthcare plan)",
    hipaa: "Free BAA + HIPAA-ready storage",
  },
};


const StatusBadge = ({ status }) => {
  if (status === "connected")
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> Connected
      </span>
    );
  if (status === "error")
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
        <AlertCircle className="w-3 h-3" /> Error
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
      Not tested
    </span>
  );
};

const ProviderCallout = ({ provider }) => {
  const h = HELP_LINKS[provider];
  return (
    <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-2">
      <div className="flex items-center gap-2 font-medium">
        <Info className="w-4 h-4 text-slate-500" /> How to set up {h.label}
      </div>
      <ol className="list-decimal pl-5 space-y-1 text-slate-700">
        <li>
          Sign up at{" "}
          <a
            href={h.signup}
            target="_blank"
            rel="noreferrer"
            className="underline text-slate-900 inline-flex items-center gap-1"
          >
            {h.signup.replace(/https?:\/\//, "")}
            <ExternalLink className="w-3 h-3" />
          </a>{" "}
          &mdash; {h.price}
        </li>
        <li>Sign the HIPAA BAA ({h.hipaa})</li>
        <li>Copy your API credentials from your {h.label} dashboard</li>
        <li>Paste them into the fields below and click Save</li>
        <li>Click &quot;Test Connection&quot; to verify</li>
      </ol>
      <a
        href={h.docs}
        target="_blank"
        rel="noreferrer"
        className="text-xs underline text-slate-500 inline-flex items-center gap-1"
      >
        View API documentation <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
};


export default function FaxProviderSettings() {
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const [activeProvider, setActiveProvider] = useState("efax");
  const [efax, setEfax] = useState({
    enabled: false,
    api_key: "",
    api_secret: "",
    account_id: "",
    sender_fax_number: "",
    inbound_fax_number: "",
    notification_emails: "",
    api_key_set: false,
    api_secret_set: false,
    status: null,
  });
  const [srfax, setSrfax] = useState({
    enabled: false,
    access_id: "",
    access_pwd: "",
    caller_id: "",
    sender_email: "",
    inbound_fax_number: "",
    notification_emails: "",
    access_id_set: false,
    access_pwd_set: false,
    status: null,
  });
  const [showEfaxSecret, setShowEfaxSecret] = useState(false);
  const [showSrfaxPwd, setShowSrfaxPwd] = useState(false);
  const [savingEfax, setSavingEfax] = useState(false);
  const [savingSrfax, setSavingSrfax] = useState(false);
  const [testingEfax, setTestingEfax] = useState(false);
  const [testingSrfax, setTestingSrfax] = useState(false);
  const [changingProvider, setChangingProvider] = useState(false);
  const [testFaxTo, setTestFaxTo] = useState("");
  const [sendingTestFax, setSendingTestFax] = useState(false);
  const [testFaxResult, setTestFaxResult] = useState(null);
  const [disableCoverSheet, setDisableCoverSheet] = useState(false);
  const [savingCoverToggle, setSavingCoverToggle] = useState(false);
  const [efaxEmail, setEfaxEmail] = useState({ enabled: false, fax_number: "", receipt_email: "", smtp_preset: "office365", smtp_host: "", smtp_port: 587, smtp_username: "", sender_email: "", presets: {}, send_domain: "efaxsend.com" });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    if (!loadedOnce.current) setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/integrations`);
      loadedOnce.current = true;
      setActiveProvider(res.data.active_fax_provider || "efax");
      setDisableCoverSheet(!!res.data.disable_cover_sheet);
      const e = res.data.efax || {};
      const s = res.data.srfax || {};
      setEfaxEmail((p) => ({ ...p, ...(res.data.efax_email || {}) }));
      setEfax((p) => ({
        ...p,
        ...e,
        api_key: e.api_key_set ? "••••••••••••••••" : "",
        api_secret: e.api_secret_set ? "••••••••••••••••" : "",
        notification_emails: e.notification_emails || "",
      }));
      setSrfax((p) => ({
        ...p,
        ...s,
        access_id: s.access_id_set ? "••••••••••••••••" : "",
        access_pwd: s.access_pwd_set ? "••••••••••••••••" : "",
        notification_emails: s.notification_emails || "",
      }));
    } catch {
      toast.error("Failed to load fax settings");
    } finally {
      setLoading(false);
    }
  };

  const setProvider = async (provider) => {
    setChangingProvider(true);
    try {
      await axios.put(`${API}/settings/integrations/active-fax-provider`, { provider });
      setActiveProvider(provider);
      toast.success(`Active fax provider set to ${HELP_LINKS[provider].label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change provider");
    } finally {
      setChangingProvider(false);
    }
  };

  const saveEfax = async () => {
    setSavingEfax(true);
    try {
      const payload = {
        enabled: efax.enabled,
        account_id: efax.account_id,
        sender_fax_number: efax.sender_fax_number,
        inbound_fax_number: efax.inbound_fax_number,
        notification_emails: efax.notification_emails,
      };
      if (efax.api_key && !efax.api_key.startsWith("••")) payload.api_key = efax.api_key;
      if (efax.api_secret && !efax.api_secret.startsWith("••"))
        payload.api_secret = efax.api_secret;
      await axios.put(`${API}/settings/integrations/efax`, payload);
      toast.success("eFax settings saved");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save eFax settings");
    } finally {
      setSavingEfax(false);
    }
  };

  const saveSrfax = async () => {
    setSavingSrfax(true);
    try {
      const payload = {
        enabled: srfax.enabled,
        caller_id: srfax.caller_id,
        sender_email: srfax.sender_email,
        inbound_fax_number: srfax.inbound_fax_number,
        notification_emails: srfax.notification_emails,
      };
      if (srfax.access_id && !srfax.access_id.startsWith("••"))
        payload.access_id = srfax.access_id;
      if (srfax.access_pwd && !srfax.access_pwd.startsWith("••"))
        payload.access_pwd = srfax.access_pwd;
      await axios.put(`${API}/settings/integrations/srfax`, payload);
      toast.success("SRFax settings saved");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save SRFax settings");
    } finally {
      setSavingSrfax(false);
    }
  };

  const testEfax = async () => {
    setTestingEfax(true);
    try {
      const res = await axios.post(`${API}/settings/integrations/efax/test`);
      if (res.data.success) toast.success("eFax connection successful");
      else toast.error(res.data.error || "eFax test failed");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "eFax test error");
    } finally {
      setTestingEfax(false);
    }
  };

  const testSrfax = async () => {
    setTestingSrfax(true);
    try {
      const res = await axios.post(`${API}/settings/integrations/srfax/test`);
      if (res.data.success) toast.success("SRFax connection successful");
      else toast.error(res.data.error || "SRFax test failed");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SRFax test error");
    } finally {
      setTestingSrfax(false);
    }
  };

  const toggleCoverSheet = async (newValue) => {
    setSavingCoverToggle(true);
    try {
      await axios.put(`${API}/settings/integrations/fax-cover-sheet`, {
        disable_cover_sheet: !newValue,
      });
      setDisableCoverSheet(!newValue);
      toast.success(newValue ? "Cover sheet enabled for all faxes" : "Cover sheet disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update cover sheet setting");
    } finally {
      setSavingCoverToggle(false);
    }
  };

  const previewCoverSheet = async () => {
    try {
      const res = await axios.get(`${API}/fax/cover-sheet/preview`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke shortly after so tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load cover sheet preview");
    }
  };

  const sendTestFax = async () => {    const to = (testFaxTo || "").trim();
    if (!to) {
      toast.error("Enter a destination fax number first");
      return;
    }
    setSendingTestFax(true);
    setTestFaxResult(null);
    try {
      const res = await axios.post(`${API}/fax/test-send`, { to_fax: to });
      setTestFaxResult(res.data);
      if (res.data.success) {
        toast.success(
          `Test fax dispatched via ${(res.data.provider || "").toUpperCase()}${
            res.data.transaction_id ? " · #" + res.data.transaction_id : ""
          }`
        );
      } else {
        toast.error(`Test fax failed: ${res.data.error || "Unknown error"}`);
      }
    } catch (e) {
      const err = e.response?.data?.detail || e.message || "Test fax error";
      setTestFaxResult({ success: false, error: err });
      toast.error(err);
    } finally {
      setSendingTestFax(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="w-5 h-5" /> Fax Provider
        </CardTitle>
        <CardDescription>
          Configure one or more fax providers. The &quot;Active Provider&quot; is what
          CarePilot will use when sending faxes. Each provider is billed separately
          by that service.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active provider picker */}
        <div className="rounded-lg border p-3 flex flex-wrap items-center gap-3 bg-slate-50">
          <div className="flex-1 min-w-0">
            <Label className="text-xs uppercase text-slate-500">
              Active Fax Provider
            </Label>
            <p className="text-sm text-slate-800">
              Currently sending faxes via{" "}
              <span className="font-semibold">
                {HELP_LINKS[activeProvider]?.label || activeProvider}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={activeProvider === "efax" ? "default" : "outline"}
              size="sm"
              disabled={changingProvider}
              onClick={() => setProvider("efax")}
              data-testid="use-efax-btn"
            >
              Use eFax
            </Button>
            <Button
              variant={activeProvider === "srfax" ? "default" : "outline"}
              size="sm"
              disabled={changingProvider}
              onClick={() => setProvider("srfax")}
              data-testid="use-srfax-btn"
            >
              Use SRFax
            </Button>
            <Button
              variant={activeProvider === "efax_email" ? "default" : "outline"}
              size="sm"
              disabled={changingProvider}
              onClick={() => setProvider("efax_email")}
              data-testid="use-efax-email-btn"
            >
              Use eFax (email)
            </Button>
          </div>
        </div>

        {/* Tabs for each provider */}
        <Tabs defaultValue={activeProvider}>
          <TabsList>
            <TabsTrigger value="efax" data-testid="fax-tab-efax">
              eFax Corporate
            </TabsTrigger>
            <TabsTrigger value="srfax" data-testid="fax-tab-srfax">
              SRFax
            </TabsTrigger>
            <TabsTrigger value="efax_email" data-testid="fax-tab-efax-email">
              eFax (regular plan)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="efax_email" className="pt-4">
            <EfaxEmailProviderTab cfg={efaxEmail} setCfg={setEfaxEmail} onSaved={fetchAll} />
          </TabsContent>

          {/* eFax tab */}
          <TabsContent value="efax" className="space-y-4 pt-4">
            <ProviderCallout provider="efax" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StatusBadge status={efax.status} />
                <span className="text-sm text-slate-600">eFax</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="efax-enabled" className="text-sm">
                  Enabled
                </Label>
                <Switch
                  id="efax-enabled"
                  checked={!!efax.enabled}
                  onCheckedChange={(v) => setEfax((p) => ({ ...p, enabled: v }))}
                  data-testid="efax-enabled-switch"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>API Key (Username)</Label>
                <Input
                  value={efax.api_key}
                  onChange={(e) => setEfax((p) => ({ ...p, api_key: e.target.value }))}
                  placeholder="Your eFax API username"
                  data-testid="efax-api-key"
                />
              </div>
              <div className="space-y-1">
                <Label>API Secret (Password)</Label>
                <div className="flex gap-2">
                  <Input
                    type={showEfaxSecret ? "text" : "password"}
                    value={efax.api_secret}
                    onChange={(e) =>
                      setEfax((p) => ({ ...p, api_secret: e.target.value }))
                    }
                    placeholder="Your eFax API password"
                    data-testid="efax-api-secret"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setShowEfaxSecret((v) => !v)}
                  >
                    {showEfaxSecret ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Account ID</Label>
                <Input
                  value={efax.account_id}
                  onChange={(e) =>
                    setEfax((p) => ({ ...p, account_id: e.target.value }))
                  }
                  placeholder="Account ID"
                />
              </div>
              <div className="space-y-1">
                <Label>Sender Fax Number</Label>
                <Input
                  value={efax.sender_fax_number}
                  onChange={(e) =>
                    setEfax((p) => ({ ...p, sender_fax_number: e.target.value }))
                  }
                  placeholder="+15551234567"
                />
              </div>
              <div className="space-y-1">
                <Label>Inbound Fax Number</Label>
                <Input
                  value={efax.inbound_fax_number}
                  onChange={(e) =>
                    setEfax((p) => ({ ...p, inbound_fax_number: e.target.value }))
                  }
                  placeholder="+15551234567"
                />
              </div>
              <div className="space-y-1">
                <Label>Notification Emails</Label>
                <Input
                  value={efax.notification_emails}
                  onChange={(e) =>
                    setEfax((p) => ({ ...p, notification_emails: e.target.value }))
                  }
                  placeholder="admin@agency.com"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={testEfax}
                disabled={testingEfax}
                data-testid="efax-test-btn"
              >
                {testingEfax ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Test Connection
              </Button>
              <Button
                onClick={saveEfax}
                disabled={savingEfax}
                data-testid="efax-save-btn"
                className="gap-2"
              >
                {savingEfax ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save eFax Settings
              </Button>
            </div>
          </TabsContent>

          {/* SRFax tab */}
          <TabsContent value="srfax" className="space-y-4 pt-4">
            <ProviderCallout provider="srfax" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StatusBadge status={srfax.status} />
                <span className="text-sm text-slate-600">SRFax</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="srfax-enabled" className="text-sm">
                  Enabled
                </Label>
                <Switch
                  id="srfax-enabled"
                  checked={!!srfax.enabled}
                  onCheckedChange={(v) => setSrfax((p) => ({ ...p, enabled: v }))}
                  data-testid="srfax-enabled-switch"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Access ID</Label>
                <Input
                  value={srfax.access_id}
                  onChange={(e) =>
                    setSrfax((p) => ({ ...p, access_id: e.target.value }))
                  }
                  placeholder="Your SRFax Access ID"
                  data-testid="srfax-access-id"
                />
              </div>
              <div className="space-y-1">
                <Label>Access Password</Label>
                <div className="flex gap-2">
                  <Input
                    type={showSrfaxPwd ? "text" : "password"}
                    value={srfax.access_pwd}
                    onChange={(e) =>
                      setSrfax((p) => ({ ...p, access_pwd: e.target.value }))
                    }
                    placeholder="Your SRFax Access Password"
                    data-testid="srfax-access-pwd"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setShowSrfaxPwd((v) => !v)}
                  >
                    {showSrfaxPwd ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Caller ID (Your Fax #)</Label>
                <Input
                  value={srfax.caller_id}
                  onChange={(e) =>
                    setSrfax((p) => ({ ...p, caller_id: e.target.value }))
                  }
                  placeholder="15551234567 (digits only)"
                />
              </div>
              <div className="space-y-1">
                <Label>Sender Email (delivery receipts)</Label>
                <Input
                  type="email"
                  value={srfax.sender_email}
                  onChange={(e) =>
                    setSrfax((p) => ({ ...p, sender_email: e.target.value }))
                  }
                  placeholder="fax@agency.com"
                />
              </div>
              <div className="space-y-1">
                <Label>Inbound Fax Number</Label>
                <Input
                  value={srfax.inbound_fax_number}
                  onChange={(e) =>
                    setSrfax((p) => ({ ...p, inbound_fax_number: e.target.value }))
                  }
                  placeholder="15551234567"
                />
              </div>
              <div className="space-y-1">
                <Label>Notification Emails</Label>
                <Input
                  value={srfax.notification_emails}
                  onChange={(e) =>
                    setSrfax((p) => ({ ...p, notification_emails: e.target.value }))
                  }
                  placeholder="admin@agency.com"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={testSrfax}
                disabled={testingSrfax}
                data-testid="srfax-test-btn"
              >
                {testingSrfax ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Test Connection
              </Button>
              <Button
                onClick={saveSrfax}
                disabled={savingSrfax}
                data-testid="srfax-save-btn"
                className="gap-2"
              >
                {savingSrfax ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save SRFax Settings
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Cover Sheet Toggle — automatically prepended to every outbound fax */}
        <div
          className="mt-6 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="cover-sheet-toggle-row"
        >
          <div>
            <div className="flex items-center gap-2">
              <Printer className="h-4 w-4 text-teal-600" />
              <h4 className="font-medium text-slate-900">Auto-attach cover sheet</h4>
              {!disableCoverSheet && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                  ON
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-600">
              Every outbound fax gets a one-page cover sheet with your agency letterhead, recipient, sender, subject, page count, and a HIPAA confidentiality notice.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={previewCoverSheet}
              className="gap-1.5"
              data-testid="preview-cover-sheet-btn"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Preview
            </Button>
            <div className="flex items-center gap-2">
              <Label htmlFor="cover-sheet-switch" className="text-sm">
                Enabled
              </Label>
              <Switch
                id="cover-sheet-switch"
                checked={!disableCoverSheet}
                disabled={savingCoverToggle}
                onCheckedChange={(v) => toggleCoverSheet(v)}
                data-testid="cover-sheet-toggle"
              />
            </div>
          </div>
        </div>

        {/* Live "Send Test Fax" panel */}
        <div
          className="mt-6 rounded-lg border border-teal-200 bg-teal-50/50 p-4"
          data-testid="test-fax-panel"
        >
          <div className="mb-2 flex items-center gap-2">
            <Send className="h-4 w-4 text-teal-600" />
            <h4 className="font-medium text-slate-900">Send a Real Test Fax</h4>
            <span className="rounded-full bg-teal-600/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-teal-700">
              via {(activeProvider || "").toUpperCase() || "provider"}
            </span>
          </div>
          <p className="mb-3 text-xs text-slate-600">
            Dispatches a one-page &quot;CarePilot Fax Test&quot; PDF through your
            active provider to verify credentials before real orders. Use a fax
            number you can access.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              placeholder="+16085551234"
              value={testFaxTo}
              onChange={(e) => setTestFaxTo(e.target.value)}
              className="sm:max-w-xs bg-white"
              data-testid="test-fax-to-input"
            />
            <Button
              onClick={sendTestFax}
              disabled={sendingTestFax || !testFaxTo.trim()}
              className="gap-2 bg-teal-600 hover:bg-teal-700"
              data-testid="send-test-fax-btn"
            >
              {sendingTestFax ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send Test Fax
            </Button>
          </div>

          {testFaxResult && (
            <div
              className={`mt-3 rounded-md border p-3 text-sm ${
                testFaxResult.success
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-900"
              }`}
              data-testid="test-fax-result"
            >
              {testFaxResult.success ? (
                <>
                  <div className="font-medium">
                    ✓ Test fax dispatched via{" "}
                    {(testFaxResult.provider || "").toUpperCase()}
                  </div>
                  {testFaxResult.transaction_id && (
                    <div className="mt-0.5 text-xs">
                      Transaction ID: {testFaxResult.transaction_id}
                    </div>
                  )}
                  <div className="mt-1 text-xs">
                    Give it 30–60 seconds to arrive at the recipient.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-medium">✗ Test fax failed</div>
                  <div className="mt-0.5 whitespace-pre-wrap break-words text-xs">
                    {testFaxResult.error || "Unknown error"}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
